import React from 'react';

// Fuelogic Logo URL
const FUELOGIC_LOGO = 'https://customer-assets.emergentagent.com/job_3dee8901-eeef-4982-bf90-3adde027bfc0/artifacts/jf8wvbtq_Fuelogic%20White%20Background%20Logo.jpg';

// Bill Template Component with 3 formats: classic, modern, compact
const BillTemplate = ({ 
  type = 'echarging', // 'echarging', 'lubricant', 'fuel'
  transaction,
  billSettings,
  pumpProfile,
  items = [] // For lubricant sales with multiple items
}) => {
  const format = billSettings?.bill_format || 'classic';

  const getBillTitle = () => {
    switch(type) {
      case 'echarging': return 'E-CHARGING INVOICE';
      case 'lubricant': return 'LUBRICANT INVOICE';
      default: return 'FUEL INVOICE';
    }
  };

  // Classic Format - Traditional centered layout
  const ClassicBill = () => (
    <div className="space-y-4 print:text-black" id="printable-bill">
      {/* Header */}
      <div className="text-center border-b-2 border-dashed pb-4">
        <div className="flex items-center justify-center gap-2 mb-2">
          <img src={FUELOGIC_LOGO} alt="Fuelogic" className="h-12 w-auto" />
        </div>
        <p className="text-xs text-muted-foreground italic">{billSettings?.fuelogic_punchline || 'Smart Fuel Management'}</p>
        {billSettings?.show_station_name && (
          <h2 className="font-bold text-lg mt-3">{pumpProfile?.pump_name}</h2>
        )}
        {billSettings?.show_station_address && pumpProfile?.address && (
          <p className="text-sm text-muted-foreground">{pumpProfile.address}</p>
        )}
        {billSettings?.show_station_mobile && pumpProfile?.mobile && (
          <p className="text-sm">Ph: {pumpProfile.mobile}</p>
        )}
        {billSettings?.show_station_gst && billSettings?.station_gstin && (
          <p className="text-xs font-mono mt-1">GSTIN: {billSettings.station_gstin}</p>
        )}
      </div>

      {/* Bill Type & Info */}
      <div className="text-center">
        <span className="inline-block px-3 py-1 bg-gray-100 dark:bg-gray-800 rounded text-sm font-semibold">
          {getBillTitle()}
        </span>
      </div>

      <div className="text-sm space-y-1">
        <div className="flex justify-between">
          <span className="text-muted-foreground">Bill No:</span>
          <span className="font-mono font-bold">{transaction.bill_number}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-muted-foreground">Date:</span>
          <span>{new Date(transaction.date).toLocaleString('en-IN')}</span>
        </div>
        {transaction.vehicle_number && (
          <div className="flex justify-between">
            <span className="text-muted-foreground">Vehicle:</span>
            <span>{transaction.vehicle_number}</span>
          </div>
        )}
        {transaction.customer_name && (
          <div className="flex justify-between">
            <span className="text-muted-foreground">Customer:</span>
            <span>{transaction.customer_name}</span>
          </div>
        )}
      </div>

      {/* Items/Charges */}
      <div className="border-t border-b border-dashed py-3 space-y-2 text-sm">
        {type === 'echarging' && (
          <>
            <div className="flex justify-between">
              <span>Energy ({transaction.energy_consumed_kwh} kWh)</span>
              <span>₹{transaction.energy_cost?.toFixed(2)}</span>
            </div>
            {transaction.idle_charges > 0 && (
              <div className="flex justify-between">
                <span>Idle Charges</span>
                <span>₹{transaction.idle_charges?.toFixed(2)}</span>
              </div>
            )}
            <div className="flex justify-between">
              <span>Service Charge</span>
              <span>₹{transaction.service_charge?.toFixed(2)}</span>
            </div>
          </>
        )}
        {type === 'lubricant' && transaction.items?.map((item, idx) => (
          <div key={idx} className="flex justify-between">
            <span>{item.product_name} ({item.pack_size_ml}ml) x{item.quantity}</span>
            <span>₹{item.total?.toFixed(2)}</span>
          </div>
        ))}
        <div className="flex justify-between border-t border-dashed pt-2">
          <span>Subtotal</span>
          <span>₹{(transaction.subtotal || 0).toFixed(2)}</span>
        </div>
        <div className="flex justify-between">
          <span>GST</span>
          <span>₹{(transaction.gst_amount || transaction.total_gst || 0).toFixed(2)}</span>
        </div>
      </div>

      {/* Total */}
      <div className="flex justify-between text-xl font-bold py-2">
        <span>TOTAL</span>
        <span>₹{(transaction.total_amount || 0).toFixed(2)}</span>
      </div>

      {/* Payment Method */}
      {transaction.payment_method && (
        <div className="text-center text-sm">
          <span className="px-2 py-1 bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 rounded">
            Paid via {transaction.payment_method.toUpperCase()}
          </span>
        </div>
      )}

      {/* Footer */}
      <div className="text-center text-xs text-muted-foreground border-t border-dashed pt-3 space-y-1">
        <p className="font-medium">{billSettings?.footer_text || 'Thank you for your business!'}</p>
        {billSettings?.terms_and_conditions && (
          <p className="text-[10px]">{billSettings.terms_and_conditions}</p>
        )}
        <p className="text-[10px] mt-2">Powered by Fuelogic</p>
      </div>
    </div>
  );

  // Modern Format - Gradient header, sleek design
  const ModernBill = () => (
    <div className="print:text-black overflow-hidden" id="printable-bill">
      {/* Gradient Header */}
      <div className="bg-gradient-to-r from-blue-600 via-blue-500 to-cyan-500 text-white p-4 -mx-6 -mt-6 mb-4">
        <div className="flex items-center gap-2 mb-1">
          <img src={FUELOGIC_LOGO} alt="Fuelogic" className="h-10 w-auto rounded bg-white p-1" />
          <span className="font-bold text-xl tracking-wider">FUELOGIC</span>
        </div>
        <p className="text-xs opacity-90">{billSettings?.fuelogic_punchline || 'Smart Fuel Management'}</p>
        {billSettings?.show_station_name && (
          <h2 className="font-bold text-lg mt-2">{pumpProfile?.pump_name}</h2>
        )}
        {billSettings?.show_station_address && pumpProfile?.address && (
          <p className="text-sm opacity-80">{pumpProfile.address}</p>
        )}
      </div>

      {/* Bill Info Card */}
      <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 mb-4">
        <div className="flex justify-between items-center">
          <div>
            <p className="text-xs text-muted-foreground uppercase tracking-wide">{getBillTitle()}</p>
            <p className="font-mono font-bold text-lg">{transaction.bill_number}</p>
          </div>
          <div className="text-right">
            <p className="text-xs text-muted-foreground">Date</p>
            <p className="text-sm">{new Date(transaction.date).toLocaleDateString('en-IN')}</p>
            <p className="text-xs text-muted-foreground">{new Date(transaction.date).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</p>
          </div>
        </div>
      </div>

      {/* Customer Info */}
      {(transaction.customer_name || transaction.vehicle_number) && (
        <div className="flex gap-4 mb-4 text-sm">
          {transaction.customer_name && (
            <div>
              <span className="text-muted-foreground">Customer: </span>
              <span className="font-medium">{transaction.customer_name}</span>
            </div>
          )}
          {transaction.vehicle_number && (
            <div>
              <span className="text-muted-foreground">Vehicle: </span>
              <span className="font-medium">{transaction.vehicle_number}</span>
            </div>
          )}
        </div>
      )}

      {/* Items */}
      <div className="space-y-2 text-sm mb-4">
        {type === 'echarging' && (
          <>
            <div className="flex justify-between py-2 border-b">
              <div>
                <span className="font-medium">Energy Consumed</span>
                <span className="text-muted-foreground ml-2">{transaction.energy_consumed_kwh} kWh</span>
              </div>
              <span>₹{transaction.energy_cost?.toFixed(2)}</span>
            </div>
            {transaction.idle_charges > 0 && (
              <div className="flex justify-between py-2 border-b">
                <span>Idle Charges</span>
                <span>₹{transaction.idle_charges?.toFixed(2)}</span>
              </div>
            )}
            <div className="flex justify-between py-2 border-b">
              <span>Service Charge</span>
              <span>₹{transaction.service_charge?.toFixed(2)}</span>
            </div>
          </>
        )}
        {type === 'lubricant' && transaction.items?.map((item, idx) => (
          <div key={idx} className="flex justify-between py-2 border-b">
            <div>
              <span className="font-medium">{item.product_name}</span>
              <span className="text-muted-foreground ml-2">{item.pack_size_ml}ml × {item.quantity}</span>
            </div>
            <span>₹{item.total?.toFixed(2)}</span>
          </div>
        ))}
      </div>

      {/* Totals Card */}
      <div className="bg-gradient-to-r from-gray-100 to-gray-50 dark:from-gray-800 dark:to-gray-700 rounded-lg p-4">
        <div className="flex justify-between text-sm mb-2">
          <span>Subtotal</span>
          <span>₹{(transaction.subtotal || 0).toFixed(2)}</span>
        </div>
        <div className="flex justify-between text-sm mb-3">
          <span>GST</span>
          <span>₹{(transaction.gst_amount || transaction.total_gst || 0).toFixed(2)}</span>
        </div>
        <div className="flex justify-between text-xl font-bold text-blue-600 dark:text-blue-400 border-t pt-3">
          <span>Total Payable</span>
          <span>₹{(transaction.total_amount || 0).toFixed(2)}</span>
        </div>
      </div>

      {/* Payment Badge */}
      {transaction.payment_method && (
        <div className="flex justify-center mt-4">
          <span className="inline-flex items-center gap-1 px-3 py-1.5 bg-green-500 text-white rounded-full text-sm font-medium">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            PAID - {transaction.payment_method.toUpperCase()}
          </span>
        </div>
      )}

      {/* Footer */}
      <div className="text-center mt-6 pt-4 border-t">
        {billSettings?.show_station_mobile && pumpProfile?.mobile && (
          <p className="text-sm mb-1">Contact: {pumpProfile.mobile}</p>
        )}
        {billSettings?.show_station_gst && billSettings?.station_gstin && (
          <p className="text-xs font-mono text-muted-foreground">GSTIN: {billSettings.station_gstin}</p>
        )}
        <p className="text-sm mt-2">{billSettings?.footer_text || 'Thank you for your business!'}</p>
        <p className="text-xs text-muted-foreground mt-1">Powered by Fuelogic</p>
      </div>
    </div>
  );

  // Compact Format - Space-efficient thermal receipt style
  const CompactBill = () => (
    <div className="font-mono text-sm print:text-black" id="printable-bill">
      {/* Header Row */}
      <div className="flex justify-between items-start border-b border-dashed pb-2 mb-2">
        <div>
          <div className="flex items-center gap-1">
            <img src={FUELOGIC_LOGO} alt="Fuelogic" className="h-6 w-auto" />
          </div>
          {billSettings?.show_station_name && (
            <p className="text-xs font-bold">{pumpProfile?.pump_name}</p>
          )}
          {billSettings?.show_station_address && pumpProfile?.address && (
            <p className="text-[10px] text-muted-foreground max-w-[150px] truncate">{pumpProfile.address}</p>
          )}
        </div>
        <div className="text-right text-xs">
          <p className="font-bold">{transaction.bill_number}</p>
          <p>{new Date(transaction.date).toLocaleDateString('en-IN')}</p>
          <p>{new Date(transaction.date).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</p>
        </div>
      </div>

      {/* Customer Row */}
      {(transaction.customer_name || transaction.vehicle_number) && (
        <div className="text-xs border-b border-dashed pb-1 mb-2">
          {transaction.customer_name && <span>{transaction.customer_name} </span>}
          {transaction.vehicle_number && <span className="text-muted-foreground">[{transaction.vehicle_number}]</span>}
        </div>
      )}

      {/* Items - Compact */}
      <div className="text-xs space-y-0.5 mb-2">
        {type === 'echarging' && (
          <>
            <div className="flex justify-between">
              <span>{transaction.energy_consumed_kwh}kWh @₹{(transaction.energy_cost/transaction.energy_consumed_kwh).toFixed(2)}</span>
              <span>{transaction.energy_cost?.toFixed(0)}</span>
            </div>
            {transaction.idle_charges > 0 && (
              <div className="flex justify-between">
                <span>IDLE</span>
                <span>{transaction.idle_charges?.toFixed(0)}</span>
              </div>
            )}
            <div className="flex justify-between">
              <span>SVC</span>
              <span>{transaction.service_charge?.toFixed(0)}</span>
            </div>
          </>
        )}
        {type === 'lubricant' && transaction.items?.map((item, idx) => (
          <div key={idx} className="flex justify-between">
            <span className="truncate max-w-[60%]">{item.quantity}x {item.product_name}</span>
            <span>{item.total?.toFixed(0)}</span>
          </div>
        ))}
      </div>

      {/* Totals - Compact */}
      <div className="border-t border-dashed pt-1 text-xs">
        <div className="flex justify-between">
          <span>SUB</span>
          <span>{(transaction.subtotal || 0).toFixed(0)}</span>
        </div>
        <div className="flex justify-between">
          <span>GST</span>
          <span>{(transaction.gst_amount || transaction.total_gst || 0).toFixed(0)}</span>
        </div>
        <div className="flex justify-between font-bold text-base border-t border-double pt-1 mt-1">
          <span>TOTAL</span>
          <span>₹{(transaction.total_amount || 0).toFixed(0)}</span>
        </div>
      </div>

      {/* Payment */}
      {transaction.payment_method && (
        <div className="text-center text-xs mt-2 border-t border-dashed pt-1">
          PAID: {transaction.payment_method.toUpperCase()}
        </div>
      )}

      {/* Footer - Minimal */}
      <div className="text-center text-[10px] text-muted-foreground mt-2 border-t border-dashed pt-2">
        {billSettings?.show_station_gst && billSettings?.station_gstin && (
          <p>GST: {billSettings.station_gstin}</p>
        )}
        <p>{billSettings?.footer_text || 'Thank you!'}</p>
        <p className="mt-1">--FUELOGIC--</p>
      </div>
    </div>
  );

  // Return appropriate format
  switch(format) {
    case 'modern':
      return <ModernBill />;
    case 'compact':
      return <CompactBill />;
    default:
      return <ClassicBill />;
  }
};

export default BillTemplate;
